import { format } from 'date-fns';
import { jsPDF } from 'jspdf';
import PropTypes from 'prop-types';

import classes from '../../commonStyles/pdf.module.css';

const GenerateQuestionnairePDF = ({
    surveyModel,
    questionnaire,
    currentQuestionnaireAnswers,
    currentParticipant,
    currentUser,
    onPdfGenerated
}) => {
    const doc = new jsPDF();

    doc.setFontSize(12);

    const createHeader = (
        surveyModel,
        questionnaire,
        currentQuestionnaireAnswers,
        currentParticipant,
        currentUser
    ) => {
        const blankRow = '<tr><td><td/></tr>';

        return `<table class="${classes.questionnaireHeader}">
            <tr>
                <td><b>Questionnaire Title</b></td>
                <td>${surveyModel.title}</td>
            </tr>
            <tr>
                <td><b>Questionnaire ID</b></td>
                <td>${questionnaire.code}</td>
            </tr>
            <tr>
                <td><b>Participant Name</b></td>
                <td>${currentParticipant.firstName} ${currentParticipant.lastName}</td>
            </tr>
            <tr>
                <td><b>Participant ID</b></td>
                <td>${currentParticipant.ptCode}</td>
            </tr>
            <tr>
                <td><b>Adviser Name</b></td>
                <td>${currentUser.firstName} ${currentUser.lastName}</td>
            </tr>
            <tr>
                <td><b>Date Complete</b></td>
                <td>${currentQuestionnaireAnswers.dateCompleted}</td>
            </tr>
            ${blankRow.repeat(20)}
        </table>`;
    };

    const createBody = (doc, surveyModel, questionnaireDetails, header) => {
        const blankRow = '<tr><td><td/></tr>';

        const pages = questionnaireDetails.data.pages;
        // calculate what page each question is on
        const questionnaireData = surveyModel.getPlainData().map((data) => {
            const page = pages.find((page) =>
                page.elements.some((entry) => entry.name === data.name)
            );
            return {
                ...data,
                page: page.title || page.name
            };
        });

        const groupedByPage = questionnaireData.reduce((acc, item) => {
            acc[item.page] = [...(acc[item.page] || []), item];
            return acc;
        }, {});

        let body = `<table class="${classes.questionnaireBody}">`;
        let oldRowCount = header.match(/<tr>/g).length;

        Object.entries(groupedByPage).forEach(([pageTitle, questions]) => {
            body += `<tr><td><h2>${pageTitle}</h2></td>`;
            questions.forEach((question) => {
                body += `<tr class="${classes.questionnaireQuestion}"><td><b>${
                    question.title || 'No question title'
                }</b></td><td class="${classes.questionnaireAnswer}">${
                    typeof question.displayValue === 'string'
                        ? !question.displayValue.includes('data:image/png')
                            ? question.displayValue
                            : '[Question Type is an Image]'
                        : '[Question Type is not Text]'
                }</td></tr>`;
            });
            body += blankRow.repeat(20);
            // calculate page sizing based on table rows
            const rowCount = body.match(/<tr>/g).length;
            if (rowCount - oldRowCount > 250) {
                oldRowCount = rowCount;
                doc.addPage();
            }
        });

        body += '</table>';

        return body;
    };

    const header = createHeader(
        surveyModel,
        questionnaire,
        currentQuestionnaireAnswers,
        currentParticipant,
        currentUser
    );

    const body = createBody(doc, surveyModel, questionnaire, header);

    const createFooter = (doc, questionnaireDetails) => {
        const pageCount = doc.internal.getNumberOfPages();
        const pageSize = doc.internal.pageSize;

        doc.setFontSize(8);

        Array.from({ length: pageCount }, (_, i) => i + 1).forEach((pageNumber) => {
            doc.setPage(pageNumber);
            const footerText =
                `    Page ${pageNumber} of ${pageCount} ` +
                ' '.repeat(110) +
                ` Printed on ${format(new Date(), 'EEEE, MMMM dd, yyyy HH:mm')} Document ID : ${
                    questionnaireDetails.code
                }`;

            doc.text(footerText, 1, pageSize.height - 5, { align: 'left' });
        });
    };

    doc.html(`<div>` + header + '<br/>' + body + '</div>', {
        async callback(doc) {
            createFooter(doc, questionnaire);
            doc.save(`${questionnaire.title}${currentQuestionnaireAnswers.dateCompleted}.pdf`);
            onPdfGenerated(true);
        },
        autoPaging: 'text',
        margin: [10, 5, 10, 5],
        width: 210,
        windowWidth: 1050
    });
};

GenerateQuestionnairePDF.propTypes = {
    surveyModel: PropTypes.object.isRequired,
    questionnaire: PropTypes.object.isRequired,
    currentQuestionnaireAnswers: PropTypes.object.isRequired,
    currentParticipant: PropTypes.object.isRequired,
    currentUser: PropTypes.object.isRequired,
    onPdfGenerated: PropTypes.func.isRequired
};

export default GenerateQuestionnairePDF;
